import React from "react";
import {
  Row,
  Col,
  FormGroup,
  Label,
  Input,
  FormFeedback,
  Button,
} from "reactstrap";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_blue.css";

// 🔹 Validation Schema (Yup)
const validationSchema = Yup.object().shape({
  name: Yup.string().required("Name is required"),

  email: Yup.string()
    .email("Invalid email format")
    .required("Email is required"),

  mobileNo: Yup.string()
    .matches(/^[6-9]\d{9}$/, "Enter a valid 10-digit mobile number")
    .required("Mobile number is required"),

  address: Yup.string().required("Address is required"),

  gstnNo: Yup.string()
    .matches(/^[0-9A-Z]{15}$/, "Enter valid GST number (15 chars)")
    .required("GST Number is required"),

  panNo: Yup.string()
    .matches(/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/, "Enter valid PAN number")
    .required("PAN Number is required"),

  startDate: Yup.string().required("Start date is required"),
  expiryDate: Yup.string().required("Expiry date is required"),

  password: Yup.string()
    .min(6, "Password must be 6+ characters")
    .required("Password is required"),
});

const AdminForm = ({ onSubmit }) => {
  return (
    <Formik
      initialValues={{
        name: "",
        email: "",
        mobileNo: "",
        address: "",
        gstnNo: "",
        panNo: "",
        startDate: "",
        expiryDate: "",
        password: "",
      }}
      validationSchema={validationSchema}
      onSubmit={(values) => {
        console.log("Form Submitted:", values);
        if (onSubmit) onSubmit(values);
      }}
    >
      {({ values, errors, touched, setFieldValue }) => (
        <Form>
          <Row className="g-6">
            {/* Name */}
            <Col md={4}>
              <FormGroup>
                <Label>Name *</Label>
                <Input
                  type="text"
                  name="name"
                  value={values.name}
                  onChange={(e) => setFieldValue("name", e.target.value)}
                  invalid={touched.name && !!errors.name}
                />
                <FormFeedback>{errors.name}</FormFeedback>
              </FormGroup>
            </Col>

            {/* Email */}
            <Col md={4}>
              <FormGroup>
                <Label>Email *</Label>
                <Input
                  type="email"
                  name="email"
                  value={values.email}
                  onChange={(e) => setFieldValue("email", e.target.value)}
                  invalid={touched.email && !!errors.email}
                />
                <FormFeedback>{errors.email}</FormFeedback>
              </FormGroup>
            </Col>

            {/* Mobile */}
            <Col md={4}>
              <FormGroup>
                <Label>Mobile No *</Label>
                <Input
                  type="text"
                  name="mobileNo"
                  maxLength="10"
                  value={values.mobileNo}
                  onChange={(e) => setFieldValue("mobileNo", e.target.value)}
                  invalid={touched.mobileNo && !!errors.mobileNo}
                />
                <FormFeedback>{errors.mobileNo}</FormFeedback>
              </FormGroup>
            </Col>

            {/* Address */}
            <Col md={4}>
              <FormGroup>
                <Label>Address *</Label>
                <Input
                  type="text"
                  name="address"
                  value={values.address}
                  onChange={(e) => setFieldValue("address", e.target.value)}
                  invalid={touched.address && !!errors.address}
                />
                <FormFeedback>{errors.address}</FormFeedback>
              </FormGroup>
            </Col>

            {/* GST Number */}
            <Col md={4}>
              <FormGroup>
                <Label>GST No *</Label>
                <Input
                  type="text"
                  name="gstnNo"
                  maxLength="15"
                  value={values.gstnNo}
                  onChange={(e) =>
                    setFieldValue("gstnNo", e.target.value.toUpperCase())
                  }
                  invalid={touched.gstnNo && !!errors.gstnNo}
                />
                <FormFeedback>{errors.gstnNo}</FormFeedback>
              </FormGroup>
            </Col>

            {/* PAN Number */}
            <Col md={4}>
              <FormGroup>
                <Label>PAN No *</Label>
                <Input
                  type="text"
                  name="panNo"
                  maxLength="10"
                  value={values.panNo}
                  onChange={(e) =>
                    setFieldValue("panNo", e.target.value.toUpperCase())
                  }
                  invalid={touched.panNo && !!errors.panNo}
                />
                <FormFeedback>{errors.panNo}</FormFeedback>
              </FormGroup>
            </Col>

            {/* Start Date */}
            <Col md={4}>
              <FormGroup>
                <Label>Start Date *</Label>
                <Flatpickr
                  className={`form-control ${
                    touched.startDate && errors.startDate ? "is-invalid" : ""
                  }`}
                  options={{ dateFormat: "d-m-Y" }}
                  value={values.startDate}
                  onChange={(date) => {
                    const formatted =
                      ("0" + date[0].getDate()).slice(-2) +
                      "-" +
                      ("0" + (date[0].getMonth() + 1)).slice(-2) +
                      "-" +
                      date[0].getFullYear();
                    setFieldValue("startDate", formatted);
                  }}
                />
                <FormFeedback>{errors.startDate}</FormFeedback>
              </FormGroup>
            </Col>

            {/* Expiry Date */}
            <Col md={4}>
              <FormGroup>
                <Label>Expiry Date *</Label>
                <Flatpickr
                  className={`form-control ${
                    touched.expiryDate && errors.expiryDate ? "is-invalid" : ""
                  }`}
                  options={{ dateFormat: "d-m-Y" }}
                  value={values.expiryDate}
                  onChange={(date) => {
                    const formatted =
                      ("0" + date[0].getDate()).slice(-2) +
                      "-" +
                      ("0" + (date[0].getMonth() + 1)).slice(-2) +
                      "-" +
                      date[0].getFullYear();
                    setFieldValue("expiryDate", formatted);
                  }}
                />
                <FormFeedback>{errors.expiryDate}</FormFeedback>
              </FormGroup>
            </Col>

            {/* Password */}
            <Col md={4}>
              <FormGroup>
                <Label>Password *</Label>
                <Input
                  type="password"
                  name="password"
                  value={values.password}
                  onChange={(e) => setFieldValue("password", e.target.value)}
                  invalid={touched.password && !!errors.password}
                />
                <FormFeedback>{errors.password}</FormFeedback>
              </FormGroup>
            </Col>

            <Col md={12} className="text-end">
              <Button color="primary" type="submit" className="btn-sm">
                Submit
              </Button>
            </Col>
          </Row>
        </Form>
      )}
    </Formik>
  );
};

export default AdminForm;
